<template>
  <div
    ref="scroller"
    data-slot="form-step-tab"
    :class="[
      'hide-scrollbar relative max-w-full overflow-x-auto',
      equalWidth || stretch ? 'w-full' : 'mx-auto w-fit',
      fadeClass,
    ]"
    @scroll="() => updateOverflow()"
  >
    <div :class="[ 'glass-switcher flex min-w-fit gap-1 rounded-md p-1 text-foreground/60', (equalWidth || stretch) && 'w-full' ]">
      <component
        :is="component"
        v-for="(item, i) in items"
        :key="'tab' + i"
        :to="toAttribute(item)"
        :class="tabClasses(i)"
        @click="() => onClick(i)"
      >
        {{ label(item) }}
      </component>
    </div>
  </div>
</template>

<script>
/**
 * Segmented step / section switcher (kit tabs look). Items are labels
 * (v-model is the active index) or { label, link } router links.
 */
export default {
  name: 'FormStepTab',
  props: {
    items: {
      type: Array,
      required: true,
    },
    modelValue: {
      type: Number,
      required: false,
      default: () => 0,
    },
    allowedIndex: {
      type: Number,
      default: () => null,
    },
    equalWidth: {
      type: Boolean,
      default: false,
    },
    // Fills the width with equal tabs on one line; scrolls sideways when they don't fit.
    stretch: {
      type: Boolean,
      default: false,
    },
  },
  emits: [ 'update:modelValue' ],
  data: () => ({
    canScrollLeft: false,
    canScrollRight: false,
  }),
  computed: {
    // Fades the edge where more tabs are hidden (phones).
    fadeClass() {
      if (this.canScrollLeft && this.canScrollRight) {
        return '[mask-image:linear-gradient(to_right,transparent,black_2rem,black_calc(100%-2rem),transparent)]';
      }
      if (this.canScrollRight) {
        return '[mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)]';
      }
      if (this.canScrollLeft) {
        return '[mask-image:linear-gradient(to_right,transparent,black_2rem)]';
      }

      return null;
    },
    isLink() {
      return typeof this.items[0] === 'object' && this.items[0].link;
    },
    component() {
      if (this.isLink) {
        return 'access-handled-router-link';
      }

      return 'div';
    },
  },
  watch: {
    modelValue() {
      this.$nextTick(() => this.scrollToActive(true));
    },
    '$route.path'() {
      this.$nextTick(() => this.scrollToActive(true));
    },
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(() => this.updateOverflow());
    this.resizeObserver.observe(this.$refs.scroller);
    this.$nextTick(() => {
      this.scrollToActive(false);
      this.updateOverflow();
    });
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect();
  },
  methods: {
    // Centers the active tab (so the next ones peek in), without scrolling the page.
    scrollToActive(smooth) {
      const el = this.$refs.scroller;
      const active = el?.querySelector('.router-link-active, .bg-primary');
      if (!active) {
        return;
      }

      el.scrollTo({
        left: active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2,
        behavior: smooth ? 'smooth' : 'instant',
      });
    },
    updateOverflow() {
      const el = this.$refs.scroller;
      if (!el) {
        return;
      }

      this.canScrollLeft = el.scrollLeft > 1;
      this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    },
    onClick(i) {
      if (this.isInactive(i)) {
        return;
      }

      this.$emit('update:modelValue', i);
    },
    tabClasses(i) {
      const isActive = i === this.modelValue && !this.isLink;

      return [
        'tab inline-flex items-center justify-center rounded-md px-3 py-2 text-xs leading-none font-medium text-inherit no-underline transition-colors hover:text-foreground',
        '[&.router-link-active]:bg-primary [&.router-link-active]:text-primary-foreground',
        this.equalWidth && 'flex-1 text-center whitespace-normal',
        this.stretch && 'flex-1 text-center whitespace-nowrap',
        !this.equalWidth && !this.stretch && 'whitespace-nowrap',
        isActive && 'bg-primary text-primary-foreground hover:text-primary-foreground',
        this.isInactive(i) ? 'cursor-not-allowed opacity-30' : 'cursor-pointer',
      ];
    },
    isInactive(i) {
      return this.allowedIndex !== null && i > this.allowedIndex;
    },
    label(item) {
      if (typeof item === 'string') {
        return item;
      }

      return item.label ?? '';
    },
    toAttribute(item) {
      if (this.isLink && !!item.link) {
        return item.link;
      }

      return null;
    },
  },
};
</script>
